import { memo, useCallback, useMemo, useEffect } from "react";
import { X, GripVertical, Plus, AlertTriangle } from "lucide-react";

const normalize = (w) => w.trim().toLowerCase();

const DEFAULT_ITEMS = [''];

const validateOrdonner = (items) => {
    const errors = [];
    const filled = items.filter(it => it?.trim());

    if (filled.length < 2) {
        errors.push('Il faut au moins 2 éléments.');
        return errors;
    }

    // Éléments vides au milieu
    const empty = items.filter(it => !it?.trim());
    if (empty.length > 0 && filled.length > 0) {
        errors.push('Certains éléments sont vides.');
    }

    // Doublons
    const normalized = filled.map(normalize);
    if (new Set(normalized).size < normalized.length) {
        errors.push('Un même élément apparaît plusieurs fois.');
    }

    return errors;
};

const OrdonnerEditor = memo(function OrdonnerEditor({ data, onDataChange, onValidChange }) {
    const items = data?.items || DEFAULT_ITEMS;

    const errors = useMemo(() => validateOrdonner(items), [items]);
    const isValid = errors.length === 0;

    useEffect(() => {
        onValidChange(isValid);
    }, [isValid, onValidChange]);

    const updateItem = useCallback((index, value) => {
        onDataChange(prev => {
            const next = [...(prev?.items || [])];
            next[index] = value;
            return { ...prev, items: next };
        });
    }, [onDataChange]);

    const addItem = useCallback(() => {
        onDataChange(prev => ({
            ...prev,
            items: [...(prev?.items || []), ''],
        }));
    }, [onDataChange]);

    const removeItem = useCallback((index) => {
        onDataChange(prev => ({
            ...prev,
            items: (prev?.items || []).filter((_, i) => i !== index),
        }));
    }, [onDataChange]);

    const filledCount = useMemo(() => items.filter(it => it?.trim()).length, [items]);

    return (
        <div className="space-y-1.5">
            {items.map((item, i) => (
                <div key={i} className="flex items-center gap-2 group">
                    <GripVertical size={12} className="text-gray-300 flex-shrink-0 cursor-grab" />
                    <span className="w-6 h-6 rounded-md bg-emerald-500 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                        {i + 1}
                    </span>
                    <input
                        type="text"
                        value={item}
                        onChange={(e) => updateItem(i, e.target.value)}
                        placeholder={`Élément ${i + 1}`}
                        className="flex-1 px-2.5 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-gray-300 min-w-0"
                    />
                    <button
                        type="button"
                        onClick={() => removeItem(i)}
                        className="opacity-0 group-hover:opacity-100 w-5 h-5 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition flex-shrink-0"
                    >
                        <X size={12} />
                    </button>
                </div>
            ))}
            <button
                type="button"
                onClick={addItem}
                className="w-full flex items-center justify-center gap-1.5 py-2 border-[1.5px] border-dashed border-gray-200 rounded-lg text-xs text-gray-400 hover:text-gray-600 hover:border-gray-300 transition"
            >
                <Plus size={12} />
                Ajouter un élément
            </button>

            <div className="flex items-center justify-between">
                <p className="text-[11px] text-gray-400">
                    Placez les éléments dans le bon ordre. L'élève les verra mélangés.
                </p>
                {filledCount > 0 && (
                    <span className={`text-[10px] font-medium ${filledCount >= 2 ? 'text-emerald-600' : 'text-amber-500'}`}>
                        {filledCount} élément{filledCount > 1 ? 's' : ''}
                    </span>
                )}
            </div>

            {errors.length > 0 && items.some(it => it?.trim()) && (
                <div className="space-y-1">
                    {errors.map((err, i) => (
                        <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                            <AlertTriangle size={11} className="text-amber-500 flex-shrink-0" />
                            <p className="text-[11px] text-amber-700">{err}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});

export default OrdonnerEditor;
